/**
 * WAV cujo proprio sinal e o payload cifrado.
 *
 * Ciphertext e indistinguivel de ruido, entao tocar o arquivo soa como
 * chiado de radio fora de estacao — que e exatamente o disfarce. PCM
 * 16 bits, mono, 24 kHz: os mesmos parametros de uma nota de voz curta.
 *
 * SO SOBREVIVE COMO DOCUMENTO. Enviado como mensagem de voz, o mensageiro
 * transcodifica para Opus e o "sinal" (isto e, o payload) vira lixo.
 */
import { ascii, concatBytes, readU32LE, requireRange, tagAt, u16le, u32le } from './binary.js'
import { stripLengthPrefix, withLengthPrefix } from './bitstream.js'
import { randomBytes } from './random.js'
import { DisguiseError } from './types.js'

const SAMPLE_RATE = 24_000
const BITS_PER_SAMPLE = 16
const CHANNELS = 1
const HEADER_BYTES = 44
/** Chiado extra no fim, para a duracao nao denunciar o tamanho exato. */
const MAX_TAIL_BYTES = 4096

export function isWav(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false
  return tagAt(bytes, 0) === 'RIFF' && tagAt(bytes, 8) === 'WAVE'
}

export function encodeWav(payload: Uint8Array): Uint8Array {
  const prefixed = withLengthPrefix(payload)
  const tailLength = 1 + (randomBytes(2)[0] ?? 0) * 16
  const tail = randomBytes(Math.min(tailLength, MAX_TAIL_BYTES))

  // Alinha em multiplo de 2: um sample de 16 bits nao fica pela metade.
  const unaligned = prefixed.length + tail.length
  const data = new Uint8Array(unaligned + (unaligned % 2))
  data.set(prefixed, 0)
  data.set(tail, prefixed.length)

  const byteRate = (SAMPLE_RATE * CHANNELS * BITS_PER_SAMPLE) / 8
  const blockAlign = (CHANNELS * BITS_PER_SAMPLE) / 8

  return concatBytes([
    ascii('RIFF'),
    u32le(HEADER_BYTES - 8 + data.length),
    ascii('WAVE'),
    ascii('fmt '),
    u32le(16),
    u16le(1), // PCM
    u16le(CHANNELS),
    u32le(SAMPLE_RATE),
    u32le(byteRate),
    u16le(blockAlign),
    u16le(BITS_PER_SAMPLE),
    ascii('data'),
    u32le(data.length),
    data,
  ])
}

export function decodeWav(bytes: Uint8Array): Uint8Array {
  if (!isWav(bytes)) throw new DisguiseError('nao e um WAV')

  let pos = 12
  while (pos + 8 <= bytes.length) {
    const id = tagAt(bytes, pos)
    const size = readU32LE(bytes, pos + 4)
    if (id === 'data') {
      requireRange(bytes, pos + 8, size)
      return stripLengthPrefix(bytes.subarray(pos + 8, pos + 8 + size))
    }
    // Chunks RIFF sao alinhados em 2 bytes; pular o byte de enchimento.
    pos += 8 + size + (size % 2)
  }
  throw new DisguiseError('WAV sem chunk de dados')
}
